import React, { useEffect, useState } from 'react';
import './login.css';
import { message } from 'antd';
import LoginForm from "./login-form";
import LanguageMenu from "../common/header/LanguageSelect";
import { useTranslation } from 'react-i18next';
import { VERDICTIA_WORDMARK_LIGHT_SRC } from "../../brand/verdictiaBrand";

function Login() {
    const { t } = useTranslation('login');
    const [heroIndex, setHeroIndex] = useState(0);
    const heroSlides = ['hero1', 'hero2', 'hero3', 'hero4'];
    const activeHero = heroSlides[heroIndex];

    useEffect(() => {
        const msg = sessionStorage.getItem("logout_message");
        const reason = sessionStorage.getItem("logout_message_reason");
        if (msg && reason === "session_expired") {
            message.error(msg);
        }
        sessionStorage.removeItem("logout_message");
        sessionStorage.removeItem("logout_message_reason");

        const registerSuccessMessage = sessionStorage.getItem("register_success_message");
        if (registerSuccessMessage) {
            const staleImmediateAccessCopy =
                /vous pouvez vous connecter|you can sign in|accès immédiat|immediate access/i.test(registerSuccessMessage);
            message.success(staleImmediateAccessCopy ? t('register.success_login_message') : registerSuccessMessage);
            sessionStorage.removeItem("register_success_message");
        }
    }, [t]);

    useEffect(() => {
        const interval = window.setInterval(() => {
            setHeroIndex((currentIndex) => (currentIndex + 1) % heroSlides.length);
        }, 5200);

        return () => window.clearInterval(interval);
    }, [heroSlides.length]);

    return (
        <div className="login-body">
            <div className="login-ambient" aria-hidden="true" />
            <div className="login-layout">

                {/* Left panel — cognitive intelligence animation */}
                <div className="left-side">
                    <div className="login-panel-texture" aria-hidden="true" />

                    <div className="header-section">
                        <div className="header-logo-login" aria-label="Verdictia">
                            <img src={VERDICTIA_WORDMARK_LIGHT_SRC} alt="Verdictia" width="174" height="58" />
                        </div>
                        <div className="language-menu-shell">
                            <LanguageMenu />
                        </div>
                    </div>

                    <div className="left-visual-stage">
                        {/*
                          VERDICTIA — "COGNITIVE CONDENSATION"
                          Story: Contract → Architecture activates → Structure resolves
                                 → Signal zones identified → Intelligence cards emerge
                                 → Decision readiness
                          Loop: 20s, seamless, no hard cuts.
                          All timing %s reference a 20s duration.
                        */}
                        <svg
                            className="verdictia-story-animation"
                            viewBox="0 0 560 560"
                            preserveAspectRatio="xMidYMid meet"
                            fill="none"
                            aria-hidden="true"
                        >
                            <defs>
                                {/* Document fill */}
                                <linearGradient id="vDocFill" x1="0%" y1="0%" x2="100%" y2="100%">
                                    <stop offset="0%" stopColor="#C7DEFF" stopOpacity="0.18" />
                                    <stop offset="100%" stopColor="#5B9BF8" stopOpacity="0.06" />
                                </linearGradient>

                                {/* Scan beam — one horizontal luminous line */}
                                <linearGradient id="vScanLine" x1="0%" y1="0%" x2="100%" y2="0%">
                                    <stop offset="0%"   stopColor="#93C5FD" stopOpacity="0" />
                                    <stop offset="40%"  stopColor="#BAE6FD" stopOpacity="0.9" />
                                    <stop offset="60%"  stopColor="#BAE6FD" stopOpacity="0.9" />
                                    <stop offset="100%" stopColor="#93C5FD" stopOpacity="0" />
                                </linearGradient>

                                {/* Signal card fill */}
                                <linearGradient id="vCardFill" x1="0%" y1="0%" x2="100%" y2="100%">
                                    <stop offset="0%" stopColor="#93C5FD" stopOpacity="0.14" />
                                    <stop offset="100%" stopColor="#38BDF8" stopOpacity="0.06" />
                                </linearGradient>

                                {/* ContractProfile node glow */}
                                <radialGradient id="vNodeGlow" cx="50%" cy="50%" r="50%">
                                    <stop offset="0%"   stopColor="#7DD3FC" stopOpacity="0.7" />
                                    <stop offset="100%" stopColor="#7DD3FC" stopOpacity="0" />
                                </radialGradient>

                                {/* Convergence pulse at center */}
                                <radialGradient id="vConverge" cx="50%" cy="50%" r="50%">
                                    <stop offset="0%"   stopColor="#BAE6FD" stopOpacity="0.55" />
                                    <stop offset="100%" stopColor="#7DD3FC" stopOpacity="0" />
                                </radialGradient>

                                {/* Soft glow — used sparingly */}
                                <filter id="vGlowSoft" x="-60%" y="-60%" width="220%" height="220%">
                                    <feGaussianBlur stdDeviation="2.5" result="blur" />
                                    <feMerge>
                                        <feMergeNode in="blur" />
                                        <feMergeNode in="SourceGraphic" />
                                    </feMerge>
                                </filter>

                                {/* Clip: scan beam constrained to document bounds */}
                                <clipPath id="vDocClip">
                                    <path d="M190 108 L338 108 L370 140 L370 422 L190 422 Z" />
                                </clipPath>
                            </defs>

                            {/* ── LAYER 0: CONVERGENCE TRACES ────────────────────────
                                Thin lines arriving from the edges of the viewBox
                                toward the document center. They "deliver" data
                                to the contract before the main animation begins.
                                Draws in fast, fades once document is established.
                            */}
                            <g className="v-edge-traces" aria-hidden="true">
                                {/* From top-left */}
                                <path className="v-trace v-trace-1"
                                    d="M20 30 L120 120 L190 160"
                                    fill="none" stroke="rgba(147, 197, 253, 0.25)" strokeWidth="0.7"
                                    strokeDasharray="4 8" strokeLinecap="round"
                                />
                                {/* From top-right */}
                                <path className="v-trace v-trace-2"
                                    d="M540 20 L440 100 L370 148"
                                    fill="none" stroke="rgba(147, 197, 253, 0.2)" strokeWidth="0.7"
                                    strokeDasharray="4 8" strokeLinecap="round"
                                />
                                {/* From left */}
                                <path className="v-trace v-trace-3"
                                    d="M0 265 L80 265 L190 260"
                                    fill="none" stroke="rgba(125, 211, 252, 0.18)" strokeWidth="0.7"
                                    strokeDasharray="3 10" strokeLinecap="round"
                                />
                                {/* From right */}
                                <path className="v-trace v-trace-4"
                                    d="M560 250 L460 255 L370 260"
                                    fill="none" stroke="rgba(125, 211, 252, 0.18)" strokeWidth="0.7"
                                    strokeDasharray="3 10" strokeLinecap="round"
                                />
                                {/* From bottom-left */}
                                <path className="v-trace v-trace-5"
                                    d="M30 540 L130 460 L190 400"
                                    fill="none" stroke="rgba(147, 197, 253, 0.2)" strokeWidth="0.7"
                                    strokeDasharray="4 8" strokeLinecap="round"
                                />
                                {/* From bottom-right */}
                                <path className="v-trace v-trace-6"
                                    d="M530 530 L440 450 L370 400"
                                    fill="none" stroke="rgba(147, 197, 253, 0.15)" strokeWidth="0.7"
                                    strokeDasharray="4 8" strokeLinecap="round"
                                />
                                {/* From top center */}
                                <path className="v-trace v-trace-7"
                                    d="M280 0 L280 50 L280 108"
                                    fill="none" stroke="rgba(186, 230, 253, 0.22)" strokeWidth="0.7"
                                    strokeDasharray="3 6" strokeLinecap="round"
                                />
                                {/* From bottom center */}
                                <path className="v-trace v-trace-8"
                                    d="M280 560 L280 500 L280 422"
                                    fill="none" stroke="rgba(186, 230, 253, 0.18)" strokeWidth="0.7"
                                    strokeDasharray="3 6" strokeLinecap="round"
                                />

                                {/* Small endpoint nodes where traces meet the document */}
                                <circle className="v-trace-node v-tnode-1" cx="190" cy="160" r="2" fill="rgba(147, 197, 253, 0.5)" />
                                <circle className="v-trace-node v-tnode-2" cx="370" cy="148" r="2" fill="rgba(147, 197, 253, 0.4)" />
                                <circle className="v-trace-node v-tnode-3" cx="190" cy="260" r="1.5" fill="rgba(125, 211, 252, 0.45)" />
                                <circle className="v-trace-node v-tnode-4" cx="370" cy="260" r="1.5" fill="rgba(125, 211, 252, 0.45)" />
                                <circle className="v-trace-node v-tnode-5" cx="190" cy="400" r="2" fill="rgba(147, 197, 253, 0.4)" />
                                <circle className="v-trace-node v-tnode-6" cx="370" cy="400" r="2" fill="rgba(147, 197, 253, 0.35)" />
                                <circle className="v-trace-node v-tnode-7" cx="280" cy="108" r="2" fill="rgba(186, 230, 253, 0.45)" />
                                <circle className="v-trace-node v-tnode-8" cx="280" cy="422" r="2" fill="rgba(186, 230, 253, 0.4)" />
                            </g>

                            {/* ── LAYER 1: COGNITIVE ARCHITECTURE RINGS ──────────────────
                                4 concentric rings emerge from the document center outward.
                                Staggered fade-in, slow rotation on dash pattern = "thinking".
                                Thin strokes, low opacity — infrastructure, not decoration.
                            */}
                            <g className="v-arch-rings" aria-hidden="true">
                                {/* Ring 1 — innermost, solid */}
                                <circle
                                    className="v-ring v-ring-1"
                                    cx="280" cy="265" r="108"
                                    stroke="rgba(147, 197, 253, 0.22)"
                                    strokeWidth="0.8"
                                />
                                {/* Ring 2 — dashed, rotates */}
                                <circle
                                    className="v-ring v-ring-2"
                                    cx="280" cy="265" r="152"
                                    stroke="rgba(147, 197, 253, 0.16)"
                                    strokeWidth="0.8"
                                    strokeDasharray="3 12"
                                />
                                {/* Ring 3 */}
                                <circle
                                    className="v-ring v-ring-3"
                                    cx="280" cy="265" r="196"
                                    stroke="rgba(125, 211, 252, 0.11)"
                                    strokeWidth="0.8"
                                    strokeDasharray="2 16"
                                />
                                {/* Ring 4 — outermost, barely visible */}
                                <circle
                                    className="v-ring v-ring-4"
                                    cx="280" cy="265" r="240"
                                    stroke="rgba(125, 211, 252, 0.07)"
                                    strokeWidth="0.8"
                                />

                                {/* 8 axis markers on ring 2 — cardinal + diagonal points
                                    Suggest structured measurement, not decoration.
                                    Tiny ticks at ring-2 intersections. */}
                                {[0, 45, 90, 135, 180, 225, 270, 315].map((deg, i) => {
                                    const r = 152;
                                    const rad = (deg * Math.PI) / 180;
                                    const cx = 280 + r * Math.cos(rad);
                                    const cy = 265 + r * Math.sin(rad);
                                    return (
                                        <circle
                                            key={i}
                                            className={`v-axis-tick v-tick-${i + 1}`}
                                            cx={cx} cy={cy} r="1.5"
                                            fill="rgba(147, 197, 253, 0.45)"
                                        />
                                    );
                                })}
                            </g>

                            {/* ── LAYER 2: CONTRACT ANCHOR ───────────────────────────────
                                The document enters once (draws in), then persists.
                                Subtle float. Lines draw with stagger after scan.
                                Document stays at center — never moves away.
                            */}
                            <g className="v-document">
                                {/* Drop shadow */}
                                <ellipse
                                    className="v-doc-shadow"
                                    cx="280" cy="432" rx="80" ry="7"
                                    fill="rgba(0, 0, 0, 0.18)"
                                />

                                {/* Document body */}
                                <path
                                    className="v-doc-sheet"
                                    d="M190 108 L338 108 L370 140 L370 422 L190 422 Z"
                                    fill="url(#vDocFill)"
                                    stroke="rgba(186, 230, 253, 0.7)"
                                    strokeWidth="1.2"
                                />

                                {/* Folded corner */}
                                <path
                                    className="v-doc-fold"
                                    d="M338 108 L338 140 L370 140"
                                    fill="rgba(186, 230, 253, 0.08)"
                                    stroke="rgba(186, 230, 253, 0.55)"
                                    strokeWidth="1"
                                    strokeLinejoin="round"
                                />

                                {/* Header: entity mark + title bar */}
                                <circle
                                    className="v-doc-mark"
                                    cx="214" cy="147" r="7"
                                    fill="none"
                                    stroke="rgba(103, 232, 249, 0.65)"
                                    strokeWidth="1.1"
                                />
                                <line
                                    className="v-doc-title"
                                    x1="228" y1="147" x2="320" y2="147"
                                    stroke="rgba(186, 230, 253, 0.5)"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                />

                                {/* Text lines — draw in stagger after document appears */}
                                <g className="v-doc-lines">
                                    <line className="v-tl v-tl-1" x1="210" y1="180" x2="348" y2="180" stroke="rgba(186, 230, 253, 0.5)" strokeWidth="2.2" strokeLinecap="round" />
                                    <line className="v-tl v-tl-2" x1="210" y1="200" x2="335" y2="200" stroke="rgba(186, 230, 253, 0.38)" strokeWidth="2.2" strokeLinecap="round" />
                                    <line className="v-tl v-tl-3" x1="210" y1="220" x2="352" y2="220" stroke="rgba(186, 230, 253, 0.48)" strokeWidth="2.2" strokeLinecap="round" />
                                    <line className="v-tl v-tl-4" x1="210" y1="248" x2="316" y2="248" stroke="rgba(186, 230, 253, 0.38)" strokeWidth="2.2" strokeLinecap="round" />
                                    <line className="v-tl v-tl-5" x1="210" y1="268" x2="350" y2="268" stroke="rgba(186, 230, 253, 0.48)" strokeWidth="2.2" strokeLinecap="round" />
                                    <line className="v-tl v-tl-6" x1="210" y1="288" x2="338" y2="288" stroke="rgba(186, 230, 253, 0.38)" strokeWidth="2.2" strokeLinecap="round" />
                                    <line className="v-tl v-tl-7" x1="210" y1="316" x2="354" y2="316" stroke="rgba(186, 230, 253, 0.48)" strokeWidth="2.2" strokeLinecap="round" />
                                    <line className="v-tl v-tl-8" x1="210" y1="336" x2="298" y2="336" stroke="rgba(186, 230, 253, 0.38)" strokeWidth="2.2" strokeLinecap="round" />
                                    <line className="v-tl v-tl-9" x1="210" y1="356" x2="345" y2="356" stroke="rgba(186, 230, 253, 0.44)" strokeWidth="2.2" strokeLinecap="round" />
                                    <line className="v-tl v-tl-10" x1="210" y1="376" x2="325" y2="376" stroke="rgba(186, 230, 253, 0.36)" strokeWidth="2.2" strokeLinecap="round" />
                                </g>
                            </g>

                            {/* ── LAYER 3: PRECISION SCAN ───────────────────────────────
                                One luminous line descends through the document.
                                No halo. No noise. Surgical.
                                Clipped to document bounds.
                            */}
                            <g clipPath="url(#vDocClip)">
                                <rect
                                    className="v-scan-beam"
                                    x="190" y="108"
                                    width="180" height="1.8"
                                    fill="url(#vScanLine)"
                                    filter="url(#vGlowSoft)"
                                />
                            </g>

                            {/* ── LAYER 4: SIGNAL ZONES ────────────────────────────────
                                3 rectangular zones highlight within the document
                                after the scan passes. These represent detected clauses /
                                contract intelligence zones — the contract-profile moment.
                                They remain visible as intelligence signals for the rest
                                of the loop.
                            */}
                            <g className="v-signal-zones">
                                <rect className="v-zone v-zone-1"
                                      x="207" y="174" width="104" height="14" rx="2"
                                      fill="rgba(103, 232, 249, 0.10)"
                                      stroke="rgba(103, 232, 249, 0.5)"
                                      strokeWidth="0.7"
                                />
                                <rect className="v-zone v-zone-2"
                                      x="207" y="242" width="130" height="14" rx="2"
                                      fill="rgba(103, 232, 249, 0.10)"
                                      stroke="rgba(103, 232, 249, 0.5)"
                                      strokeWidth="0.7"
                                />
                                <rect className="v-zone v-zone-3"
                                      x="207" y="310" width="88" height="14" rx="2"
                                      fill="rgba(103, 232, 249, 0.10)"
                                      stroke="rgba(103, 232, 249, 0.5)"
                                      strokeWidth="0.7"
                                />
                            </g>

                            {/* ── LAYER 5: SIGNAL CONNECTOR PATHS ─────────────────────
                                Thin lines project from each signal zone outward to the
                                three intelligence cards. They draw in like circuits.
                                Dashed, directional, minimal.
                            */}
                            <g className="v-connectors">
                                {/* Zone 1 → left card */}
                                <path
                                    className="v-conn v-conn-1"
                                    d="M207 181 L152 181 L108 222"
                                    fill="none"
                                    stroke="rgba(103, 232, 249, 0.3)"
                                    strokeWidth="0.8"
                                    strokeDasharray="3 5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                                {/* Zone 2 → right card */}
                                <path
                                    className="v-conn v-conn-2"
                                    d="M337 249 L395 249 L432 222"
                                    fill="none"
                                    stroke="rgba(103, 232, 249, 0.3)"
                                    strokeWidth="0.8"
                                    strokeDasharray="3 5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                                {/* Zone 3 → bottom card */}
                                <path
                                    className="v-conn v-conn-3"
                                    d="M280 422 L280 458"
                                    fill="none"
                                    stroke="rgba(103, 232, 249, 0.3)"
                                    strokeWidth="0.8"
                                    strokeDasharray="3 5"
                                    strokeLinecap="round"
                                />
                            </g>

                            {/* ── LAYER 6: INTELLIGENCE CARDS ──────────────────────────
                                Three cards emerge at: upper-left, upper-right, bottom.
                                Each contains 2–3 data lines (structured intelligence).
                                No icons. No checkmarks. Just condensed signal lines.
                                The card shape itself communicates format + precision.
                            */}
                            <g className="v-cards">

                                {/* Card A — upper left — "Qualification" */}
                                <g className="v-card v-card-a">
                                    <rect x="30" y="192" width="138" height="64" rx="8"
                                          fill="url(#vCardFill)"
                                          stroke="rgba(103, 232, 249, 0.42)"
                                          strokeWidth="0.9"
                                          filter="url(#vGlowSoft)"
                                    />
                                    {/* Thin top accent line = card "status" */}
                                    <line x1="38" y1="192" x2="96" y2="192"
                                          stroke="rgba(103, 232, 249, 0.8)"
                                          strokeWidth="1.6"
                                          strokeLinecap="round"
                                    />
                                    <line className="v-cl v-cl-a1" x1="46" y1="213" x2="148" y2="213" stroke="rgba(186, 230, 253, 0.55)" strokeWidth="2" strokeLinecap="round" />
                                    <line className="v-cl v-cl-a2" x1="46" y1="230" x2="132" y2="230" stroke="rgba(186, 230, 253, 0.4)"  strokeWidth="2" strokeLinecap="round" />
                                    <line className="v-cl v-cl-a3" x1="46" y1="247" x2="141" y2="247" stroke="rgba(186, 230, 253, 0.35)" strokeWidth="2" strokeLinecap="round" />
                                </g>

                                {/* Card B — upper right — "Risk signals" */}
                                <g className="v-card v-card-b">
                                    <rect x="392" y="188" width="138" height="64" rx="8"
                                          fill="url(#vCardFill)"
                                          stroke="rgba(103, 232, 249, 0.42)"
                                          strokeWidth="0.9"
                                          filter="url(#vGlowSoft)"
                                    />
                                    <line x1="400" y1="188" x2="458" y2="188"
                                          stroke="rgba(103, 232, 249, 0.8)"
                                          strokeWidth="1.6"
                                          strokeLinecap="round"
                                    />
                                    <line className="v-cl v-cl-b1" x1="408" y1="209" x2="514" y2="209" stroke="rgba(186, 230, 253, 0.55)" strokeWidth="2" strokeLinecap="round" />
                                    <line className="v-cl v-cl-b2" x1="408" y1="226" x2="498" y2="226" stroke="rgba(186, 230, 253, 0.4)"  strokeWidth="2" strokeLinecap="round" />
                                    <line className="v-cl v-cl-b3" x1="408" y1="243" x2="506" y2="243" stroke="rgba(186, 230, 253, 0.35)" strokeWidth="2" strokeLinecap="round" />
                                </g>

                                {/* Card C — bottom center — "Decision readiness" */}
                                <g className="v-card v-card-c">
                                    <rect x="194" y="458" width="172" height="58" rx="8"
                                          fill="url(#vCardFill)"
                                          stroke="rgba(103, 232, 249, 0.42)"
                                          strokeWidth="0.9"
                                          filter="url(#vGlowSoft)"
                                    />
                                    <line x1="202" y1="458" x2="272" y2="458"
                                          stroke="rgba(103, 232, 249, 0.8)"
                                          strokeWidth="1.6"
                                          strokeLinecap="round"
                                    />
                                    <line className="v-cl v-cl-c1" x1="210" y1="477" x2="350" y2="477" stroke="rgba(186, 230, 253, 0.55)" strokeWidth="2" strokeLinecap="round" />
                                    <line className="v-cl v-cl-c2" x1="210" y1="494" x2="322" y2="494" stroke="rgba(186, 230, 253, 0.4)"  strokeWidth="2" strokeLinecap="round" />
                                </g>
                            </g>

                            {/* ── LAYER 7: CONVERGENCE PULSE ───────────────────────────
                                Final moment: a soft radial pulse at the document center
                                = "decision ready". Appears once near end of loop.
                                Quiet, intentional, terminal.
                            */}
                            <circle
                                className="v-convergence"
                                cx="280" cy="265" r="60"
                                fill="url(#vConverge)"
                            />

                        </svg>

                        <div className="login-hero-message" key={activeHero}>
                            <h1>{t(`${activeHero}.title`)}</h1>
                            <p>{t(`${activeHero}.subtitle`)}</p>
                        </div>
                    </div>
                </div>

                {/* Right panel — login form */}
                <div className="right-side">
                    <div className="form-container">
                        <LoginForm />
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Login;
